/**
 * ECW 対話 callable wrapper hooks (Phase A、旧仕様 5-step master prompt 完全踏襲)。
 *
 * 旧 ECW (`old/apps/user/src/components/ecw/IdeaForm.tsx` + `lib/ecwAiClient.ts`) と
 * `old_template/ai-dialogue_ecw-standard_2026-05-14.json` に揃えた 3 つの callable wrapper。
 *
 *   - `useGenerateEcwDialogue()`        — step0..step4 共通 (引数 step + vars)
 *   - `useGenerateEcwTitleSuggestions()` — タイトル案 3 件生成
 *   - `useGenerateEcwInsightSummary()`   — back モード時の Insight 要約
 *
 * v2 オリジナル (`insight-step1` 等) は廃止、旧通り step0..step4 に揃える。
 * UI は Phase B で 3 ペイン構成に書き換え予定 (本ファイルは callable 接続のみ)。
 */

import { httpsCallable } from 'firebase/functions';
import { useCallback, useState } from 'react';
import type { EcwIdeaReference } from '@citizen-walk/shared-types';
import { functions } from '../../lib/firebase';

/* ===== step 共通の型 ===== */

export type EcwDialogueStep = 'step0' | 'step1' | 'step2' | 'step3' | 'step4';

export interface EcwStep0Result {
  introMessage: string;
}
export interface EcwStep1Result {
  analysis: string;
  resolutionQuestions: string[];
}
export interface EcwStep2Result {
  diagnosisQuestions: string[];
}
export interface EcwStep3Result {
  actionQuestions: string[];
  references: EcwIdeaReference[];
}
export interface EcwStep4Result {
  effectQuestions: string[];
}

/** callable `generateEcwDialogue` の input。step ごとに必要な placeholder 入力を渡す。 */
export interface GenerateEcwDialogueInput {
  step: EcwDialogueStep;
  districtId: string;
  aiDialogueSettingsId?: string | null;

  /** {{FACTS}} (step1, step2 用、posts 整形済文字列)。 */
  facts?: string;
  /**
   * {{CATEGORY_MASTER}} — {{FACTS}} に出現した分類コードだけに絞った対応表
   * (`buildCategoryMasterBlock` で組立済)。プロンプトに placeholder が無ければ無視される。
   */
  categoryMaster?: string;
  /** {{EMOTION}}。 */
  emotion?: string;
  /** {{CATEGORY}}。 */
  categoryLabel?: string;
  /** {{QUESTIONS}} (aiReferences から組立済)。 */
  questions?: string;
  /** {{REFERENCES}} (step3 用、aiReferences から組立済)。 */
  references?: string;
  /** {{USER_INITIAL_THOUGHTS}} — step1 用。 */
  userInitialThoughts?: string;
  /** {{USER_OBSERVATIONS}} — step2, step3 用。 */
  userObservations?: string;
  /** {{USER_DIAGNOSIS}} — step3 用。 */
  userDiagnosis?: string;
  /** {{USER_ACTION}} — step4 用。 */
  userAction?: string;
}

export type GenerateEcwDialogueResult =
  | { ok: true; step: 'step0'; settingsId: string; result: EcwStep0Result; nextStepId: 'step1' }
  | { ok: true; step: 'step1'; settingsId: string; result: EcwStep1Result; nextStepId: 'step2' }
  | { ok: true; step: 'step2'; settingsId: string; result: EcwStep2Result; nextStepId: 'step3' }
  | { ok: true; step: 'step3'; settingsId: string; result: EcwStep3Result; nextStepId: 'step4' }
  | { ok: true; step: 'step4'; settingsId: string; result: EcwStep4Result; nextStepId: null }
  | { ok: false; error: string };

export type GenerateEcwDialogueState =
  | { status: 'idle'; error: null }
  | { status: 'pending'; error: null }
  | { status: 'success'; result: GenerateEcwDialogueResult; error: null }
  | { status: 'error'; error: Error };

/** 1 dialog thread の途中で出力された各 step の結果を保持する thread state。 */
export interface EcwDialogueThread {
  step0?: EcwStep0Result | null;
  step1?: EcwStep1Result | null;
  step2?: EcwStep2Result | null;
  step3?: EcwStep3Result | null;
  step4?: EcwStep4Result | null;
}

function normalizeCallableError(e: unknown): Error {
  const raw = e as { code?: string; message?: string };
  const code = typeof raw.code === 'string' ? raw.code : '';
  let message = raw.message ?? String(e);
  if (code.includes('unauthenticated')) {
    message = 'AI 生成には認証が必要です。';
  }
  const err = new Error(message);
  (err as Error & { code?: string }).code = code;
  return err;
}

export function useGenerateEcwDialogue(): {
  state: GenerateEcwDialogueState;
  /** Step ごとに独立した loading state (UI の step indicator 用)。 */
  stepStatus: Record<EcwDialogueStep, 'idle' | 'pending' | 'success' | 'error'>;
  /** 直近で成功した各 step 出力を保持する thread。 */
  thread: EcwDialogueThread;
  generate: (input: GenerateEcwDialogueInput) => Promise<GenerateEcwDialogueResult>;
  reset: () => void;
} {
  const [state, setState] = useState<GenerateEcwDialogueState>({ status: 'idle', error: null });
  const [stepStatus, setStepStatus] = useState<
    Record<EcwDialogueStep, 'idle' | 'pending' | 'success' | 'error'>
  >({
    step0: 'idle',
    step1: 'idle',
    step2: 'idle',
    step3: 'idle',
    step4: 'idle',
  });
  const [thread, setThread] = useState<EcwDialogueThread>({});

  const generate = useCallback(async (input: GenerateEcwDialogueInput) => {
    setState({ status: 'pending', error: null });
    setStepStatus((prev) => ({ ...prev, [input.step]: 'pending' }));
    try {
      const fn = httpsCallable<GenerateEcwDialogueInput, GenerateEcwDialogueResult>(
        functions,
        'generateEcwDialogue',
      );
      const res = await fn(input);
      const data = res.data;
      setState({ status: 'success', result: data, error: null });
      if (data.ok) {
        setStepStatus((prev) => ({ ...prev, [data.step]: 'success' }));
        if (data.step === 'step0') setThread((t) => ({ ...t, step0: data.result }));
        else if (data.step === 'step1') setThread((t) => ({ ...t, step1: data.result }));
        else if (data.step === 'step2') setThread((t) => ({ ...t, step2: data.result }));
        else if (data.step === 'step3') setThread((t) => ({ ...t, step3: data.result }));
        else if (data.step === 'step4') setThread((t) => ({ ...t, step4: data.result }));
      } else {
        setStepStatus((prev) => ({ ...prev, [input.step]: 'error' }));
      }
      return data;
    } catch (e) {
      const err = normalizeCallableError(e);
      setState({ status: 'error', error: err });
      setStepStatus((prev) => ({ ...prev, [input.step]: 'error' }));
      throw err;
    }
  }, []);

  const reset = useCallback(() => {
    setState({ status: 'idle', error: null });
    setStepStatus({
      step0: 'idle',
      step1: 'idle',
      step2: 'idle',
      step3: 'idle',
      step4: 'idle',
    });
    setThread({});
  }, []);

  return { state, stepStatus, thread, generate, reset };
}

/* ============================================================================
 * generateEcwTitleSuggestions hook
 * ========================================================================== */

export interface GenerateEcwTitleSuggestionsInput {
  districtId: string;
  /** 起動時の selected posts のタイトル配列。 */
  postTitles: string[];
  emotion: string;
  categoryLabel: string;
}

export type GenerateEcwTitleSuggestionsResult =
  | { ok: true; titles: string[] }
  | { ok: false; error: string };

export type GenerateEcwTitleSuggestionsState =
  | { status: 'idle'; error: null }
  | { status: 'pending'; error: null }
  | { status: 'success'; result: GenerateEcwTitleSuggestionsResult; error: null }
  | { status: 'error'; error: Error };

export function useGenerateEcwTitleSuggestions(): {
  state: GenerateEcwTitleSuggestionsState;
  generate: (input: GenerateEcwTitleSuggestionsInput) => Promise<GenerateEcwTitleSuggestionsResult>;
  reset: () => void;
} {
  const [state, setState] = useState<GenerateEcwTitleSuggestionsState>({
    status: 'idle',
    error: null,
  });

  const generate = useCallback(async (input: GenerateEcwTitleSuggestionsInput) => {
    setState({ status: 'pending', error: null });
    try {
      const fn = httpsCallable<GenerateEcwTitleSuggestionsInput, GenerateEcwTitleSuggestionsResult>(
        functions,
        'generateEcwTitleSuggestions',
      );
      const res = await fn(input);
      setState({ status: 'success', result: res.data, error: null });
      return res.data;
    } catch (e) {
      const err = normalizeCallableError(e);
      setState({ status: 'error', error: err });
      throw err;
    }
  }, []);

  const reset = useCallback(() => setState({ status: 'idle', error: null }), []);

  return { state, generate, reset };
}

/* ============================================================================
 * generateEcwInsightSummary hook
 * ========================================================================== */

export interface GenerateEcwInsightSummaryInput {
  districtId: string;
  /** 呼び出し側で `感情: X, カテゴリ: Y, 症状: Z, 診断: W` 形式に整形済みの Insight テキスト配列。 */
  insightTexts: string[];
}

export type GenerateEcwInsightSummaryResult =
  | { ok: true; summary: string }
  | { ok: false; error: string };

export type GenerateEcwInsightSummaryState =
  | { status: 'idle'; error: null }
  | { status: 'pending'; error: null }
  | { status: 'success'; result: GenerateEcwInsightSummaryResult; error: null }
  | { status: 'error'; error: Error };

export function useGenerateEcwInsightSummary(): {
  state: GenerateEcwInsightSummaryState;
  generate: (input: GenerateEcwInsightSummaryInput) => Promise<GenerateEcwInsightSummaryResult>;
  reset: () => void;
} {
  const [state, setState] = useState<GenerateEcwInsightSummaryState>({
    status: 'idle',
    error: null,
  });

  const generate = useCallback(async (input: GenerateEcwInsightSummaryInput) => {
    setState({ status: 'pending', error: null });
    try {
      const fn = httpsCallable<GenerateEcwInsightSummaryInput, GenerateEcwInsightSummaryResult>(
        functions,
        'generateEcwInsightSummary',
      );
      const res = await fn(input);
      setState({ status: 'success', result: res.data, error: null });
      return res.data;
    } catch (e) {
      const err = normalizeCallableError(e);
      setState({ status: 'error', error: err });
      throw err;
    }
  }, []);

  const reset = useCallback(() => setState({ status: 'idle', error: null }), []);

  return { state, generate, reset };
}
